import {
  BadgeCheck,
  Cart,
  EmptyPage,
  Group,
  Laptop,
  Packages,
  Page,
  Tools,
  User,
} from 'iconoir-react';

import { CREATE_MODAL_TYPES } from '@savgroup-front-common/constants/control';
import { MessageType } from '@savgroup-front-common/types';

import { ROUTES, SPARE_PART_ROUTES } from '@/constants/routing';

import { TITLE_MESSAGES_BY_CREATE_MODAL_TYPE } from './CreateButton.constants';
import { CREATE_MENU_ITEM_TYPE, CreateMenuItem } from './CreateButton.types';
import messages from './messages';

const getTitleMessageByOption = (option: CREATE_MODAL_TYPES) => {
  const result = TITLE_MESSAGES_BY_CREATE_MODAL_TYPE[option];

  if (!result) {
    throw new Error(`The option: ${option} is not handled on getTitleMessage.`);
  }

  return result;
};

export const getAuthorizedOptions = (
  authorizations: CREATE_MODAL_TYPES[],
  onClick: (val: CreateMenuItem) => void,
): CreateMenuItem[] => {
  const { authorizedOptions } = authorizations.reduce<{
    previousTitle: MessageType | null;
    authorizedOptions: CreateMenuItem[];
  }>(
    (acc, authorization, index) => {
      const title = getTitleMessageByOption(authorization);
      const addedValues = [...acc.authorizedOptions];

      if (title && acc.previousTitle !== title) {
        addedValues.push({
          type: CREATE_MENU_ITEM_TYPE.TITLE,
          label: title,
          key: `${title.id}_${index}`,
        });
      }

      switch (authorization) {
        case CREATE_MODAL_TYPES.FILE_ORDER_REFERENCE:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.BUTTON,
            key: CREATE_MODAL_TYPES.FILE_ORDER_REFERENCE,
            label: messages.orderReference,
            icon: <Cart />,
            onClick,
          });
          break;
        case CREATE_MODAL_TYPES.FILE_PRODUCT_REFERENCE:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.BUTTON,
            key: CREATE_MODAL_TYPES.FILE_PRODUCT_REFERENCE,
            label: messages.productReference,
            icon: <Laptop />,
            onClick,
          });
          break;
        case CREATE_MODAL_TYPES.FILE_BY_CUSTOMER_INFORMATION:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.BUTTON,
            key: CREATE_MODAL_TYPES.FILE_BY_CUSTOMER_INFORMATION,
            label: messages.customerInformation,
            icon: <User />,
            onClick,
          });
          break;
        case CREATE_MODAL_TYPES.FILE_OWNER_STOCK:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.BUTTON,
            key: CREATE_MODAL_TYPES.FILE_OWNER_STOCK,
            label: messages.fileOwnerStock,
            icon: <Packages />,
            onClick,
          });
          break;
        case CREATE_MODAL_TYPES.FILE_RECEIPT_INFORMATION:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.BUTTON,
            key: CREATE_MODAL_TYPES.FILE_RECEIPT_INFORMATION,
            label: messages.receiptInformation,
            icon: <Page />,
            onClick,
          });
          break;
        case CREATE_MODAL_TYPES.SPARE_PART_REQUEST:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.LINK,
            key: CREATE_MODAL_TYPES.SPARE_PART_REQUEST,
            label: messages.sparePartRequest,
            icon: <Tools />,
            href: SPARE_PART_ROUTES.CREATE_REQUEST,
          });
          break;

        case CREATE_MODAL_TYPES.SELLER:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.BUTTON,
            key: CREATE_MODAL_TYPES.SELLER,
            label: messages.seller,
            icon: <BadgeCheck />,
            onClick,
          });
          break;

        case CREATE_MODAL_TYPES.TENANT:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.BUTTON,
            key: CREATE_MODAL_TYPES.TENANT,
            label: messages.tenant,
            icon: <Group />,
            onClick,
          });
          break;
        case CREATE_MODAL_TYPES.NEW_CREATE_FILE:
          addedValues.push({
            type: CREATE_MENU_ITEM_TYPE.LINK,
            key: CREATE_MODAL_TYPES.NEW_CREATE_FILE,
            label: messages.newCreateFile,
            icon: <EmptyPage />,
            href: ROUTES.NEW_CREATE_FILE,
          });
          break;

        default:
          throw new Error(
            `The authorization: ${authorization} is not handled.`,
          );
      }

      return {
        ...acc,
        previousTitle: title,
        authorizedOptions: addedValues,
      };
    },
    {
      previousTitle: null,
      authorizedOptions: [],
    },
  );

  return authorizedOptions;
};
